import React from 'react';
import {
    Box,
    Heading,
    Text,
    Stack,
    List,
    ListItem,
    ListIcon,
    Button,
    Image,
    Divider,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';

const sectionStyle = {
    px: { base: 6, md: 12 },
    py: { base: 12, md: 20 },
};

const LymphaticDrainage = () => {
    return (
        <Box fontFamily="Georgia, serif">

            {/* 🔷 Hero Banner */}
            <Box bg="#3D5940" {...sectionStyle} >
                <Box maxW="6xl" mx="auto" mt={10}>
                    <Image
                        src="/assets/images/lymphatic-drainage.png"
                        alt="Lymphatic Drainage Therapy"
                        w="100%"
                        h={{ base: '200px', md: '400px' }}
                        objectFit="cover"
                        borderRadius="xl"
                        boxShadow="lg"
                        mb={10}
                    />
                    <Heading size="lg" color="#ffffff" textAlign="center" mb={4}>
                        What is Lymphatic Drainage?
                    </Heading>
                    <Text color="#ffffff" fontSize="lg" maxW="3xl" mx="auto" textAlign="center">
                        Lymphatic Drainage Therapy is a gentle technique designed to stimulate the body’s lymphatic system. This promotes the natural flow of lymph — a fluid that carries toxins, waste, and immune cells. It helps reduce inflammation, improve circulation, and boost detoxification through targeted massage or machine-assisted stimulation.
                    </Text>
                </Box>
            </Box>

            {/* ✅ Benefits */}
            <Box bg="#E6E9D8" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="green.800" mb={6} textAlign="center">
                        Key Benefits
                    </Heading>
                    <List spacing={3} color="gray.700" fontSize="md">
                        <ListItem><ListIcon as={CheckCircleIcon} color="green.600" /> Reduces swelling and water retention</ListItem>
                        <ListItem><ListIcon as={CheckCircleIcon} color="green.600" /> Improves immune function and detox</ListItem>
                        <ListItem><ListIcon as={CheckCircleIcon} color="green.600" /> Enhances skin tone and recovery</ListItem>
                        <ListItem><ListIcon as={CheckCircleIcon} color="green.600" /> Aids in post-surgery or injury healing</ListItem>
                    </List>
                </Box>
            </Box>

            {/* 🔹 How It Works */}
            <Box bg="white" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="green.800" mb={4}>How It Works</Heading>
                    <Text color="gray.700" fontSize="md">
                        Lymphatic drainage can be done manually or with specialized equipment. Manual therapy involves rhythmic, circular strokes over lymph nodes and vessels to encourage fluid movement. Equipment-based options like pressotherapy use air compression suits or rollers to stimulate the same effect in a more automated, efficient way.
                    </Text>
                </Box>
            </Box>

            {/* 🔹 Ideal Candidates */}
            <Box bg="#F1F6EF" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="green.800" mb={4}>Who Should Consider This Therapy?</Heading>
                    <Text color="gray.700" fontSize="md" mb={2}>It’s ideal for individuals experiencing:</Text>
                    <List spacing={2} pt={2} color="gray.700" fontSize="md">
                        <ListItem>• Swelling or fluid retention</ListItem>
                        <ListItem>• Low immunity or recurring infections</ListItem>
                        <ListItem>• Poor circulation and sluggish metabolism</ListItem>
                        <ListItem>• Post-surgery recovery or detox support</ListItem>
                    </List>
                </Box>
            </Box>

            {/* 🔹 Contraindications */}
            <Box bg="#FFF9F0" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="red.700" mb={4}>Contraindications</Heading>
                    <List spacing={3} fontSize="md" color="gray.700">
                        <ListItem><ListIcon as={WarningIcon} color="red.500" /> Acute infections or fever</ListItem>
                        <ListItem><ListIcon as={WarningIcon} color="red.500" /> Congestive heart failure</ListItem>
                        <ListItem><ListIcon as={WarningIcon} color="red.500" /> Active blood clots (thrombosis)</ListItem>
                        <ListItem><ListIcon as={WarningIcon} color="red.500" /> Untreated cancer (without medical advice)</ListItem>
                    </List>
                </Box>
            </Box>

            {/* 🔹 Our Safety and Expertise */}
            <Box bg="#E6E9D8" {...sectionStyle}>
                <Box maxW="6xl" mx="auto">
                    <Heading size="md" color="green.800" mb={4}>Our Expertise & Safety Protocol</Heading>
                    <Text color="gray.700" fontSize="md">
                        All sessions are administered by trained professionals using FDA-approved machines or certified manual techniques. We customize the intensity and duration based on your needs, ensuring safety, hygiene, and optimal therapeutic results.
                    </Text>
                </Box>
            </Box>

            {/* 🔸 CTA */}
            <Box bg="white" {...sectionStyle}>
                <Box textAlign="center" maxW="3xl" mx="auto">
                    <Heading size="md" color="green.800" mb={2}>Boost Immunity & Cleanse Naturally</Heading>
                    <Text mb={4} color="gray.600">
                        Schedule your Lymphatic Drainage session and feel the lightness from within.
                    </Text>
                    <Button size="lg" colorScheme="green">Book Now</Button>
                </Box>
            </Box>

            <Divider mt={16} />
        </Box >
    );
};

export default LymphaticDrainage;
